/**
 * Helpers for GTFS wheelchair boarding display on stop quai labels and popups.
 */

import { getGtfsStopIdSuffix, normalizeGtfsParentStationId } from "../../../tools/gtfsStopId.tools";
import { StopWheelchairBoarding, type StopDto } from "../../../types/dtos/api/stop.dto";

/** Resolved wheelchair boarding state shown to the user (unknown values are omitted). */
export type StopWheelchairBoardingDisplay = "accessible" | "notAccessible";

/** Violet fill for the wheelchair pictogram. */
export const STOP_WHEELCHAIR_ICON_COLOR = "#7C3AED";

/** Red strike used when boarding is explicitly not accessible. */
export const STOP_WHEELCHAIR_STRIKE_COLOR = "#DC2626";

/**
 * Maps a GTFS wheelchair boarding enum to a UI display state.
 *
 * @param value Raw wheelchair boarding from a stop DTO or API field.
 * @returns `"accessible"`, `"notAccessible"`, or `null` when information is missing.
 */
export const resolveStopWheelchairBoardingDisplay = ( value: StopWheelchairBoarding | null | undefined ): StopWheelchairBoardingDisplay | null => {
    if ( value === StopWheelchairBoarding.Accessible ) {
        return "accessible";
    }

    if ( value === StopWheelchairBoarding.NotAccessible ) {
        return "notAccessible";
    }

    return null;
};

/**
 * Parses wheelchair boarding from a MapLibre GeoJSON feature property.
 *
 * @param value Raw `properties.wheelchairBoarding` (string or number).
 * @returns Parsed enum value, or `null` when absent or invalid.
 */
export const parseGeoJsonWheelchairBoardingProperty = ( value: unknown ): StopWheelchairBoarding | null => {
    if ( value === null || value === undefined || value === "" ) {
        return null;
    }

    const numericValue = typeof value === "number" ? value : Number( value );

    if ( Number.isNaN( numericValue ) ) {
        return null;
    }

    const knownValues = new Set<number>( [
        StopWheelchairBoarding.NoInformation,
        StopWheelchairBoarding.Accessible,
        StopWheelchairBoarding.NotAccessible,
    ] );

    if ( knownValues.has( numericValue ) ) {
        return numericValue as StopWheelchairBoarding;
    }

    return null;
};

/**
 * Checks whether a child stop belongs to the clicked parent station.
 *
 * @param child Child stop from the preload store.
 * @param parentStopId Parent stop id from the map click.
 * @returns Whether the child's `parentStation` references the parent stop.
 */
const childStopBelongsToParent = ( child: StopDto, parentStopId: string ): boolean => {
    const childParentStation = child.parentStation?.trim() ?? "";

    if ( childParentStation === "" ) {
        return false;
    }

    const parentGtfsSource = parentStopId.includes( "_" ) ? parentStopId.split( "_" )[0] ?? null : null;
    const parentSuffix = normalizeGtfsParentStationId( getGtfsStopIdSuffix( parentStopId, parentGtfsSource ) );
    const normalizedChildParent = normalizeGtfsParentStationId( childParentStation );

    return normalizedChildParent === parentSuffix ||
      childParentStation === parentSuffix ||
      parentStopId.endsWith( normalizedChildParent );
};

/**
 * Resolves wheelchair boarding for a platform under a parent station using preloaded child stops.
 *
 * @param children Child stops currently held in the preload store.
 * @param parentStopId Parent stop id selected on the map.
 * @param platformCode Rider-facing platform code (quai).
 * @returns Wheelchair boarding for the matching child stop, or `null` when not found.
 */
export const findChildStopWheelchairBoardingByPlatform = (
    children: StopDto[],
    parentStopId: string,
    platformCode: string,
): StopWheelchairBoarding | null => {
    if ( platformCode === "" ) {
        return null;
    }

    for ( const child of children ) {
        if ( ( child.platformCode ?? "" ) !== platformCode ) {
            continue;
        }

        if ( !childStopBelongsToParent( child, parentStopId ) ) {
            continue;
        }

        return child.wheelchairBoarding;
    }

    return null;
};
